import { BuildClass, DoAsync, guid, runPromisesSequentially, } from '../../../../universal';
import { _, match, React } from '../../../lib';
import { Alerts } from '../../component-main';
import { svrtsRequest } from '../../svrts-interface';
import { CJSX, useRSInstance } from '../meta-types';
import { stubCanvas, stubDiv, stubInput, stubTextarea } from '../stubs';
import { UsageLimitDonut } from '../usage-limit-donut';
import { isValidImageFile, processImage } from './image-utils';
import { createEmptyInputState, getAttachmentType, MAX_ATTACHMENTS, } from './types';
import { startWaveformAnimation } from './waveform';
const AUDIO_FFT_SIZE = 2048;
const CONTEXT_TOKEN_CAP = 160000;
const readFileAsBase64 = async (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
        if (typeof reader.result === 'string') {
            const base64 = reader.result.split(',')[1] ?? '';
            resolve(base64);
        }
        else {
            reject(new Error('Failed to read file as string'));
        }
    };
    reader.onerror = () => {
        reject(new Error(reader.error?.message ?? 'Unknown reader error'));
    };
    reader.readAsDataURL(file);
});
const arrayBufferToBase64 = (buffer) => {
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    const chunks = _.range(0, bytes.length, chunkSize).map(start => String.fromCharCode(...bytes.subarray(start, start + chunkSize)));
    return btoa(chunks.join(''));
};
const buildAttachmentBlocks = async (attachments, timestamp) => _.compact(await runPromisesSequentially(attachments.map(att => async () => {
    if (att.type === 'image' && att.preview) {
        return {
            role: 'user',
            timestamp,
            type: 'image-pending',
            image: {
                data: att.preview.replace(/^data:image\/\w+;base64,/, ''),
                filename: att.filename,
                width: att.dimensions?.width ?? 0,
                height: att.dimensions?.height ?? 0,
            },
        };
    }
    if (att.type === 'pdf' || att.type === 'xlsx') {
        try {
            const base64 = await readFileAsBase64(att.file);
            return {
                role: 'user',
                timestamp,
                type: 'file-pending',
                file: {
                    data: base64,
                    filename: att.filename,
                    mimeType: att.file.type,
                    size: att.file.size,
                },
            };
        }
        catch (err) {
            console.error('Failed to read file:', err);
            Alerts.Alert({ msg: `Failed to read file: ${att.filename}` });
        }
    }
    return null;
})));
export const ThreadTextBox = (props) => {
    const rs = useRSInstance({
        props,
        defaultState: React.useCallback((p) => ({
            input: p.value,
            audioAnalyser: null,
            audioContext: null,
            mediaRecorder: null,
            audioChunks: [],
        }), []),
        refs: {
            container: React.useRef(stubDiv),
            textarea: React.useRef(stubTextarea),
            canvas: React.useRef(stubCanvas),
            fileInput: React.useRef(stubInput),
        },
        actionToDelta: null,
    });
    React.useEffect(() => {
        rs.updateState({ input: props.value });
    }, [props.value, rs]);
    React.useEffect(() => {
        if (props.textareaRef && rs.refs.textarea.current) {
            ;
            props.textareaRef.current =
                rs.refs.textarea.current;
        }
    }, [props.textareaRef, rs.refs.textarea]);
    React.useEffect(() => {
        const container = rs.refs.container.current;
        const textarea = rs.refs.textarea.current;
        if (!container || !textarea) {
            return;
        }
        const resizeTextarea = () => {
            textarea.style.height = 'auto';
            textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
        };
        const observer = new ResizeObserver(() => {
            resizeTextarea();
        });
        observer.observe(container);
        return () => {
            observer.disconnect();
        };
    }, [rs.refs.container, rs.refs.textarea]);
    const hasText = rs.state.input.text.trim().length > 0;
    const hasAttachments = rs.state.input.attachments.length > 0;
    const hasContent = hasText || hasAttachments;
    const hasAttachmentOnly = hasAttachments && !hasText;
    const isRecording = rs.state.input.voice.status === 'recording';
    const isTranscribing = rs.state.input.voice.status === 'transcribing';
    const contextReadTokens = Math.max(0, rs.props.contextReadTokens ?? 0);
    const updateInput = (partial) => {
        const newInput = { ...rs.state.input, ...partial };
        rs.updateState({ input: newInput });
        rs.props.onUpdate(newInput);
    };
    const handleSend = React.useCallback(async () => {
        if (!hasContent || rs.props.disabled) {
            return;
        }
        const content = [];
        const timestamp = new Date().toISOString();
        if (rs.state.input.text.trim()) {
            content.push({
                role: 'user',
                timestamp,
                type: 'text',
                text: rs.state.input.text.trim(),
            });
        }
        content.push(...(await buildAttachmentBlocks(rs.state.input.attachments, timestamp)));
        rs.props.onSend(content, false);
        const emptyInput = createEmptyInputState();
        rs.updateState({ input: emptyInput });
        rs.props.onUpdate(emptyInput);
        const textarea = rs.refs.textarea.current;
        if (textarea) {
            textarea.style.height = 'auto';
        }
    }, [rs, hasContent]);
    const autoSent = React.useRef(false);
    React.useEffect(() => {
        if (!rs.props.autoSendOnMount) {
            return;
        }
        if (autoSent.current) {
            return;
        }
        if (!hasContent || rs.props.disabled) {
            return;
        }
        if (isRecording || isTranscribing) {
            return;
        }
        autoSent.current = true;
        handleSend().catch(console.error);
    }, [
        handleSend,
        hasContent,
        isRecording,
        isTranscribing,
        rs.props.autoSendOnMount,
        rs.props.disabled,
    ]);
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend().catch(err => {
                console.error(err);
            });
        }
    };
    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const recorder = new MediaRecorder(stream, {
                audioBitsPerSecond: 64000,
                mimeType: 'audio/webm',
            });
            const audioContext = new AudioContext();
            const analyser = audioContext.createAnalyser();
            analyser.fftSize = AUDIO_FFT_SIZE;
            const source = audioContext.createMediaStreamSource(stream);
            source.connect(analyser);
            const chunks = [];
            recorder.addEventListener('dataavailable', e => {
                chunks.push(e.data);
            });
            rs.updateState({
                mediaRecorder: recorder,
                audioAnalyser: analyser,
                audioContext: audioContext,
                audioChunks: chunks,
            });
            updateInput({
                voice: { status: 'recording', startTime: Date.now() },
            });
            recorder.start();
            startWaveform(analyser);
        }
        catch (err) {
            console.error('Failed to start recording:', err);
            Alerts.Alert({ msg: 'Could not access microphone' });
        }
    };
    const stopRecording = (action) => {
        const recorder = rs.state.mediaRecorder;
        if (!recorder) {
            return;
        }
        const chunks = rs.state.audioChunks;
        recorder.addEventListener('stop', () => {
            if (action === 'cancel' || chunks.length === 0) {
                updateInput({ voice: { status: 'idle' } });
                return;
            }
            const audioBlob = new Blob(chunks, { type: 'audio/webm' });
            if (action === 'send') {
                updateInput({ voice: { status: 'transcribing', audioBlob } });
                transcribeAudioAndSend(audioBlob).catch(err => {
                    console.error(err);
                });
            }
            else {
                updateInput({ voice: { status: 'transcribing', audioBlob } });
                transcribeAudio(audioBlob).catch(err => {
                    console.error(err);
                });
            }
        });
        recorder.stop();
        recorder.stream.getTracks().forEach(t => {
            t.stop();
        });
        rs.state.audioAnalyser?.disconnect();
        rs.state.audioContext?.close().catch(err => {
            console.error('Failed to close audio context:', err);
        });
        rs.updateState({
            mediaRecorder: null,
            audioAnalyser: null,
            audioContext: null,
            audioChunks: [],
        });
    };
    const transcribeAudio = async (audioBlob) => {
        const audioBuffer = await audioBlob.arrayBuffer();
        const audioBase64 = arrayBufferToBase64(audioBuffer);
        svrtsRequest({
            data: {
                funcID: [0, 51],
                audio: audioBase64,
                prompt: rs.props.voicePrompt ?? null,
            },
            any: () => { },
            no: data => {
                Alerts.Alert({ msg: data.message });
                updateInput({ voice: { status: 'idle' } });
            },
            yes: data => {
                const newText = rs.state.input.text
                    ? `${rs.state.input.text}\n${data.text}`
                    : data.text;
                updateInput({
                    text: newText,
                    voice: { status: 'idle' },
                });
            },
        });
    };
    const transcribeAudioAndSend = async (audioBlob) => {
        const audioBuffer = await audioBlob.arrayBuffer();
        const audioBase64 = arrayBufferToBase64(audioBuffer);
        svrtsRequest({
            data: {
                funcID: [0, 51],
                audio: audioBase64,
                prompt: rs.props.voicePrompt ?? null,
            },
            any: () => { },
            no: data => {
                Alerts.Alert({ msg: data.message });
                updateInput({ voice: { status: 'idle' } });
            },
            yes: data => {
                DoAsync(async () => {
                    const content = [];
                    const timestamp = new Date().toISOString();
                    if (data.text.trim()) {
                        content.push({
                            role: 'user',
                            timestamp,
                            type: 'text',
                            text: data.text.trim(),
                        });
                    }
                    content.push(...(await buildAttachmentBlocks(rs.state.input.attachments, timestamp)));
                    if (content.length > 0) {
                        rs.props.onSend(content, false);
                    }
                    const emptyInput = createEmptyInputState();
                    rs.updateState({ input: emptyInput });
                    rs.props.onUpdate(emptyInput);
                }).catch(err => {
                    console.error(err);
                });
            },
        });
    };
    const startWaveform = (analyser) => {
        const canvas = rs.refs.canvas.current;
        if (!canvas) {
            return;
        }
        startWaveformAnimation({
            canvas,
            analyser,
            shouldContinue: () => rs.state.audioAnalyser !== null,
        });
    };
    const handleFileSelect = async (files) => {
        if (!files || files.length === 0) {
            return;
        }
        const currentCount = rs.state.input.attachments.length;
        const remaining = MAX_ATTACHMENTS - currentCount;
        if (remaining <= 0) {
            Alerts.Alert({ msg: `Maximum ${MAX_ATTACHMENTS} attachments allowed` });
            return;
        }
        const newAttachments = _.compact(await runPromisesSequentially(_.chain(Array.from(files))
            .slice(0, remaining)
            .map(file => async () => {
            const type = getAttachmentType(file.type);
            if (!type) {
                Alerts.Alert({ msg: `Unsupported file type: ${file.name}` });
                return null;
            }
            let preview = null;
            let width = undefined;
            let height = undefined;
            if (type === 'image') {
                if (!isValidImageFile(file)) {
                    Alerts.Alert({ msg: `Invalid image file: ${file.name}` });
                    return null;
                }
                try {
                    const processed = await processImage(file);
                    preview = `data:image/webp;base64,${processed.data}`;
                    width = processed.width;
                    height = processed.height;
                }
                catch (err) {
                    console.error('Failed to process image:', err);
                    Alerts.Alert({
                        msg: `Failed to process image: ${file.name}`,
                    });
                    return null;
                }
            }
            return {
                id: guid(),
                file,
                type,
                preview,
                filename: file.name,
                dimensions: width && height ? { width, height } : undefined,
            };
        })
            .value()));
        if (newAttachments.length > 0) {
            updateInput({
                attachments: [...rs.state.input.attachments, ...newAttachments],
            });
        }
    };
    const lastExternalFilesRef = React.useRef(null);
    React.useEffect(() => {
        if (props.externalFiles &&
            props.externalFiles.length > 0 &&
            props.externalFiles !== lastExternalFilesRef.current) {
            lastExternalFilesRef.current = props.externalFiles;
            handleFileSelect(props.externalFiles).catch(console.error);
        }
    }, [handleFileSelect, props.externalFiles]);
    const removeAttachment = (id) => {
        updateInput({
            attachments: rs.state.input.attachments.filter(a => a.id !== id),
        });
    };
    const handlePaste = (e) => {
        const items = e.clipboardData?.items;
        if (!items) {
            return;
        }
        const imageFiles = _.chain(Array.from(items))
            .map(item => {
            if (!item.type.startsWith('image/')) {
                return null;
            }
            return item.getAsFile() ?? null;
        })
            .compact()
            .value();
        if (imageFiles.length > 0) {
            e.preventDefault();
            const dt = new DataTransfer();
            imageFiles.forEach(f => {
                dt.items.add(f);
            });
            handleFileSelect(dt.files).catch(console.error);
        }
    };
    return (React.createElement("div", { ref: rs.refs.container, className: BuildClass({
            relative: true,
        }) },
        React.createElement(CJSX, { cond: isTranscribing },
            React.createElement("div", { className: "mb-1.5 text-xs text-center text-[oklch(var(--chat-l-text-muted)_0_0)] animate-pulse" }, "Transcribing audio...")),
        React.createElement(CJSX, { cond: rs.state.input.attachments.length > 0 },
            React.createElement(AttachmentPreviews, { attachments: rs.state.input.attachments, onRemove: removeAttachment })),
        React.createElement(TextboxShine, { disabled: rs.props.disabled, isRecording: isRecording, darkMode: rs.props.darkMode, showFloatShadow: rs.props.showFloatShadow },
            React.createElement("canvas", { ref: rs.refs.canvas, width: 800, height: 100, className: BuildClass({
                    'absolute inset-y-0 left-0 z-10': true,
                    hidden: !isRecording,
                }), style: {
                    width: 'calc(100% - 130px)',
                    ['--waveform-bg']: 'oklch(var(--chat-l-surface) 0 0)',
                    ['--waveform-stroke']: 'oklch(var(--chat-l-accent) var(--chat-c-accent) var(--chat-hue))',
                } }),
            React.createElement("textarea", { ref: rs.refs.textarea, rows: 1, className: BuildClass({
                    'w-full resize-none py-2.5 pl-3 pr-32': true,
                    'bg-transparent': true,
                    'text-base text-[oklch(var(--chat-l-text)_0_0)]': true,
                    'placeholder:text-[oklch(var(--chat-l-text-muted)_0_0)]': true,
                    'focus:outline-none': true,
                    'opacity-0': isRecording,
                }), style: { minHeight: '44px', maxHeight: '200px' }, placeholder: rs.props.placeholder ?? 'Type your message...', value: rs.state.input.text, onChange: e => {
                    updateInput({ text: e.target.value });
                    const ta = e.target;
                    ta.style.height = 'auto';
                    ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`;
                }, onKeyDown: handleKeyDown, onPaste: handlePaste, disabled: isRecording || isTranscribing }),
            React.createElement("div", { className: "absolute top-1/2 -translate-y-1/2 right-1.5 flex items-center gap-0.5 z-20" },
                !isRecording && React.createElement(ContextUsageWheel, { tokensRead: contextReadTokens }),
                !isRecording && !rs.props.disabled && (React.createElement("button", { type: "button", onClick: () => rs.refs.fileInput.current?.click(), disabled: rs.state.input.attachments.length >= MAX_ATTACHMENTS, className: BuildClass({
                        'w-9 h-9 rounded-full transition-colors flex items-center justify-center cursor-pointer': true,
                        'hover:bg-[oklch(var(--chat-l-text)_0_0_/_0.06)]': true,
                        'text-[oklch(var(--chat-l-text-muted)_0_0)]': true,
                        'disabled:opacity-30 disabled:cursor-not-allowed': true,
                    }), title: "Attach file" },
                    React.createElement("svg", { className: "w-[18px] h-[18px]", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", strokeWidth: 2 },
                        React.createElement("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" })))),
                rs.props.disabled && (React.createElement(ProcessingButtons, { onStop: () => {
                        console.warn('Stop button clicked - abort signal not yet implemented');
                    } })),
                !rs.props.disabled && (React.createElement(VoiceSendButton, { hasContent: hasContent, hasAttachmentOnly: hasAttachmentOnly, isRecording: isRecording, isTranscribing: isTranscribing, disabled: false, onStartRecording: startRecording, onStopRecording: stopRecording, onSend: handleSend }))),
            React.createElement("input", { ref: rs.refs.fileInput, type: "file", multiple: true, accept: "image/*,.pdf,.xlsx,.xls", className: "hidden", onChange: e => {
                    handleFileSelect(e.target.files).catch(err => {
                        console.error(err);
                    });
                    e.target.value = '';
                } }))));
};
const TextboxShine = (props) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const floatShadow = match([props.showFloatShadow, props.darkMode])
        .with([true, true], () => '0 -8px 24px oklch(0 0 0 / 0.5)')
        .with([true, false], () => '0 -6px 20px oklch(0 0 0 / 0.4)')
        .otherwise(() => '');
    const darkModeStyle = {
        background: 'oklch(var(--chat-l-surface) 0 0)',
        borderTop: isFocused
            ? '1px solid oklch(1 0 0 / 0.2)'
            : '1px solid oklch(1 0 0 / 0.12)',
        borderLeft: '1px solid oklch(1 0 0 / 0.08)',
        borderRight: '1px solid oklch(1 0 0 / 0.08)',
        borderBottom: '1px solid oklch(1 0 0 / 0.04)',
        boxShadow: [
            isFocused
                ? '0 2px 8px oklch(0 0 0 / 0.4), inset 0 1px 0 oklch(1 0 0 / 0.05)'
                : '0 2px 6px oklch(0 0 0 / 0.3), inset 0 1px 0 oklch(1 0 0 / 0.03)',
            floatShadow,
        ]
            .filter(Boolean)
            .join(', '),
    };
    const lightModeStyle = {
        background: 'oklch(var(--chat-l-surface) 0 0)',
        border: isFocused
            ? '1px solid oklch(0 0 0 / 0.25)'
            : '1px solid oklch(0 0 0 / 0.12)',
        boxShadow: [
            isFocused ? '0 1px 3px oklch(0 0 0 / 0.08)' : '0 1px 2px oklch(0 0 0 / 0.05)',
            floatShadow,
        ]
            .filter(Boolean)
            .join(', '),
    };
    return (React.createElement("div", { className: "relative rounded-2xl transition-all duration-200", onFocusCapture: () => {
            setIsFocused(true);
        }, onBlurCapture: () => {
            setIsFocused(false);
        }, style: props.darkMode ? darkModeStyle : lightModeStyle }, props.children));
};
const ContextUsageWheel = (props) => {
    const safeTokensRead = Number.isFinite(props.tokensRead) ? props.tokensRead : 0;
    return (React.createElement("div", { className: "w-7 h-7 rounded-full flex items-center justify-center" },
        React.createElement(UsageLimitDonut, { value: safeTokensRead, max: CONTEXT_TOKEN_CAP, tooltipContent: React.createElement("div", { style: { fontSize: '13px', lineHeight: '1.2', textAlign: 'left' } }, `Context usage: ${safeTokensRead.toLocaleString()} / ${CONTEXT_TOKEN_CAP.toLocaleString()} tokens`), size: 16, thickness: 4, className: "cursor-help" })));
};
const ProcessingButtons = (props) => (React.createElement("div", { className: "flex items-center gap-0.5" },
    React.createElement("div", { className: "w-9 h-9 rounded-full flex items-center justify-center text-[oklch(var(--chat-l-text-muted)_0_0)]" },
        React.createElement("svg", { className: "w-[18px] h-[18px] animate-spin", fill: "none", viewBox: "0 0 24 24" },
            React.createElement("circle", { className: "opacity-25", cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "3" }),
            React.createElement("path", { className: "opacity-75", fill: "currentColor", d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" }))),
    React.createElement("button", { type: "button", onClick: props.onStop, className: BuildClass({
            'w-9 h-9 rounded-full transition-colors flex items-center justify-center cursor-pointer': true,
            'bg-[oklch(var(--chat-l-bg)_0_0)]': true,
            'border border-[oklch(var(--chat-l-text)_0_0_/_0.2)]': true,
            'hover:bg-[oklch(var(--chat-l-text)_0_0_/_0.08)]': true,
            'text-[oklch(var(--chat-l-text-muted)_0_0)]': true,
        }), title: "Stop generating (TODO: wire up abort signal)" },
        React.createElement("svg", { className: "w-[16px] h-[16px]", fill: "currentColor", viewBox: "0 0 24 24" },
            React.createElement("rect", { x: "6", y: "6", width: "12", height: "12", rx: "2" })))));
const VoiceSendButton = (props) => {
    if (props.isRecording) {
        return (React.createElement("div", { className: "flex items-center gap-0.5" },
            React.createElement("button", { type: "button", onClick: () => {
                    props.onStopRecording('cancel');
                }, className: BuildClass({
                    'w-9 h-9 rounded-full transition-colors flex items-center justify-center cursor-pointer': true,
                    'bg-red-100 hover:bg-red-200 text-red-600': true,
                }), title: "Cancel recording" },
                React.createElement("svg", { className: "w-[18px] h-[18px]", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", strokeWidth: 2 },
                    React.createElement("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M6 18L18 6M6 6l12 12" }))),
            React.createElement("button", { type: "button", onClick: () => {
                    props.onStopRecording('transcribe');
                }, className: BuildClass({
                    'w-9 h-9 rounded-full transition-colors flex items-center justify-center cursor-pointer': true,
                    'bg-[oklch(var(--chat-l-bg)_0_0)]': true,
                    'border border-[oklch(var(--chat-l-text)_0_0_/_0.2)]': true,
                    'hover:bg-[oklch(var(--chat-l-text)_0_0_/_0.08)]': true,
                    'text-[oklch(var(--chat-l-text-subtle)_0_0)]': true,
                }), title: "Stop and transcribe to text" },
                React.createElement("svg", { className: "w-[18px] h-[18px]", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", strokeWidth: 2 },
                    React.createElement("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" }))),
            React.createElement("button", { type: "button", onClick: () => {
                    props.onStopRecording('send');
                }, className: BuildClass({
                    'w-9 h-9 rounded-full transition-colors flex items-center justify-center cursor-pointer': true,
                    'bg-[oklch(var(--chat-l-accent)_var(--chat-c-accent)_var(--chat-hue))]': true,
                    'hover:bg-[oklch(calc(var(--chat-l-accent)-0.05)_var(--chat-c-accent)_var(--chat-hue))]': true,
                    'text-white': true,
                    'animate-pulse': true,
                }), title: "Stop and send immediately" },
                React.createElement("svg", { className: "w-[18px] h-[18px]", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", strokeWidth: 2.5 },
                    React.createElement("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M5 10l7-7m0 0l7 7m-7-7v18" })))));
    }
    if (props.hasAttachmentOnly) {
        return (React.createElement("div", { className: "flex items-center gap-0.5" },
            React.createElement("button", { type: "button", onClick: () => {
                    props.onStartRecording().catch(err => {
                        console.error(err);
                    });
                }, disabled: props.disabled || props.isTranscribing, className: BuildClass({
                    'w-9 h-9 rounded-full transition-colors flex items-center justify-center cursor-pointer': true,
                    'hover:bg-[oklch(var(--chat-l-text)_0_0_/_0.06)]': true,
                    'text-[oklch(var(--chat-l-text-muted)_0_0)]': true,
                    'disabled:opacity-30 disabled:cursor-not-allowed': true,
                }), title: "Add voice message" },
                React.createElement("svg", { className: "w-[18px] h-[18px]", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", strokeWidth: 2 },
                    React.createElement("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" }))),
            React.createElement("button", { type: "button", onClick: () => {
                    props.onSend().catch(err => {
                        console.error(err);
                    });
                }, disabled: props.disabled || props.isTranscribing, className: BuildClass({
                    'w-9 h-9 rounded-full transition-all flex items-center justify-center cursor-pointer': true,
                    'bg-[oklch(var(--chat-l-accent)_var(--chat-c-accent)_var(--chat-hue))]': true,
                    'hover:bg-[oklch(calc(var(--chat-l-accent)-0.05)_var(--chat-c-accent)_var(--chat-hue))]': true,
                    'text-white': true,
                    'disabled:opacity-50 disabled:cursor-not-allowed': true,
                }), title: "Send attachments" },
                React.createElement("svg", { className: "w-[18px] h-[18px]", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", strokeWidth: 2.5 },
                    React.createElement("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M5 10l7-7m0 0l7 7m-7-7v18" })))));
    }
    if (props.hasContent) {
        return (React.createElement("button", { type: "button", onClick: () => {
                props.onSend().catch(err => {
                    console.error(err);
                });
            }, disabled: props.disabled || props.isTranscribing, className: BuildClass({
                'w-9 h-9 rounded-full transition-all flex items-center justify-center cursor-pointer': true,
                'bg-[oklch(var(--chat-l-accent)_var(--chat-c-accent)_var(--chat-hue))]': true,
                'hover:bg-[oklch(calc(var(--chat-l-accent)-0.05)_var(--chat-c-accent)_var(--chat-hue))]': true,
                'text-white': true,
                'disabled:opacity-50 disabled:cursor-not-allowed': true,
            }), title: "Send message" },
            React.createElement("svg", { className: "w-[18px] h-[18px]", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", strokeWidth: 2.5 },
                React.createElement("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M5 10l7-7m0 0l7 7m-7-7v18" }))));
    }
    return (React.createElement("button", { type: "button", onClick: () => {
            props.onStartRecording().catch(err => {
                console.error(err);
            });
        }, disabled: props.disabled || props.isTranscribing, className: BuildClass({
            'w-9 h-9 rounded-full transition-colors flex items-center justify-center cursor-pointer': true,
            'hover:bg-[oklch(var(--chat-l-text)_0_0_/_0.06)]': true,
            'text-[oklch(var(--chat-l-text-muted)_0_0)]': true,
            'disabled:opacity-30 disabled:cursor-not-allowed': true,
        }), title: "Record voice message" },
        React.createElement("svg", { className: "w-[18px] h-[18px]", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", strokeWidth: 2 },
            React.createElement("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" }))));
};
const AttachmentPreviews = (props) => (React.createElement("div", { className: "flex flex-wrap gap-2 mb-2" }, props.attachments.map(att => (React.createElement("div", { key: att.id, className: BuildClass({
        'relative group': true,
        'rounded-xl overflow-hidden': true,
        'border border-[oklch(1_0_0_/_0.2)]': true,
        'bg-[oklch(var(--chat-l-surface)_0_0_/_0.4)]': true,
        'backdrop-blur-md': true,
        'shadow-[0_2px_8px_rgba(0,0,0,0.08),inset_0_1px_0_rgba(255,255,255,0.15)]': true,
    }) },
    att.type === 'image' && att.preview ? (React.createElement("img", { src: att.preview, alt: att.filename, className: "w-16 h-16 object-cover" })) : (React.createElement("div", { className: "w-16 h-16 flex items-center justify-center" }, att.type === 'pdf' ? (React.createElement("svg", { className: "w-8 h-8 text-red-500", fill: "currentColor", viewBox: "0 0 24 24" },
        React.createElement("path", { d: "M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 2l5 5h-5V4zM9 13h2v5H9v-5zm4 0h2v5h-2v-5z" }))) : (React.createElement("svg", { className: "w-8 h-8 text-green-600", fill: "currentColor", viewBox: "0 0 24 24" },
        React.createElement("path", { d: "M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 2l5 5h-5V4zM8 17h8v-2H8v2zm0-4h8v-2H8v2z" }))))),
    React.createElement("div", { className: BuildClass({
            'px-1 py-0.5 text-[10px] truncate max-w-16': true,
            'text-[oklch(var(--chat-l-text-subtle)_0_0)]': true,
            'bg-[oklch(var(--chat-l-bg)_0_0_/_0.5)]': true,
        }), title: att.filename }, att.filename),
    React.createElement("button", { type: "button", onClick: () => {
            props.onRemove(att.id);
        }, className: BuildClass({
            'absolute -top-1 -right-1 cursor-pointer': true,
            'w-5 h-5 rounded-full': true,
            'bg-red-500 text-white': true,
            'flex items-center justify-center': true,
            'opacity-0 group-hover:opacity-100 transition-opacity': true,
            'hover:bg-red-600': true,
        }), title: "Remove attachment" },
        React.createElement("svg", { className: "w-3 h-3", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor" },
            React.createElement("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 3, d: "M6 18L18 6M6 6l12 12" }))))))));
//# sourceMappingURL=thread-textbox.js.map